import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  UserCheck,
  Clock,
  UserX,
  Eye,
  Edit2,
  Trash2,
  Mail,
  Globe,
  MapPin,
  Calendar,
  Shield,
  Briefcase,
  AlertTriangle,
  ChevronDown,
  X,
  Check,
} from "react-feather";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getUsersAPI,
  getUserAPI,
  createUserAPI,
  updateUserAPI,
  deleteUserAPI,
  getRolesAPI,
  getDepartmentsAPI,
} from "../../../api/api";
import PageHeader from "../../../Components/shared/PageHeader";
import DataTableWrapper from "../../../Components/shared/DataTableWrapper";
import StatusBadge from "../../../Components/shared/StatusBadge";
import EmptyState from "../../../Components/shared/EmptyState";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./UserList.module.scss";

const PRIMARY_COLOR = "#644998";

// ─── Custom Multi-Select Dropdown (Fixed - No unwanted re-renders) ────────────
function MultiSelect({
  options,
  value,
  onChange,
  placeholder,
  labelKey = "label",
  valueKey = "value",
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = useCallback(
    (val) => {
      if (value.includes(val)) {
        onChange(value.filter((v) => v !== val));
      } else {
        onChange([...value, val]);
      }
    },
    [value, onChange],
  );

  const selectedLabels = useMemo(() => {
    return options
      .filter((o) => value.includes(o[valueKey]))
      .map((o) => o[labelKey]);
  }, [options, value, labelKey, valueKey]);

  return (
    <div className={styles.multiSelect} ref={containerRef}>
      <button
        type="button"
        className={styles.multiSelectTrigger}
        onClick={() => setOpen(!open)}
        ref={triggerRef}
      >
        <span className={styles.multiSelectValue}>
          {selectedLabels.length === 0 ? (
            <span className={styles.multiSelectPlaceholder}>{placeholder}</span>
          ) : (
            selectedLabels.map((lbl, i) => (
              <span key={i} className={styles.multiSelectChip}>
                {lbl}
              </span>
            ))
          )}
        </span>
        <ChevronDown
          size={14}
          className={`${styles.multiSelectChevron} ${open ? styles.open : ""}`}
        />
      </button>
      {open && (
        <div className={styles.multiSelectDropdown}>
          {options.map((opt) => {
            const isSelected = value.includes(opt[valueKey]);
            return (
              <div
                key={opt[valueKey]}
                className={`${styles.multiSelectOption} ${isSelected ? styles.selected : ""}`}
                onClick={() => toggle(opt[valueKey])}
              >
                <span className={styles.multiSelectCheck}>
                  {isSelected && <Check size={12} />}
                </span>
                {opt[labelKey]}
              </div>
            );
          })}
          {options.length === 0 && (
            <div className={styles.multiSelectEmpty}>No options</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Form Field ──────────────────────────────────────────────────────────────
function FormField({ label, children, required }) {
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>
        {label} {required && <span className={styles.required}>*</span>}
      </label>
      {children}
    </div>
  );
}

// ── Shared modal body for create/edit ──
function UserForm({
  isEdit,
  inviteForm,
  setInviteForm,
  roleOptions,
  deptOptions,
}) {
  return (
    <div className={styles.formGrid}>
      <FormField label="Full Name" required={!isEdit}>
        <input
          className={styles.input}
          type="text"
          value={inviteForm.name}
          onChange={(e) =>
            setInviteForm((s) => ({ ...s, name: e.target.value }))
          }
        />
      </FormField>

      <FormField label="Email" required={!isEdit}>
        <input
          className={styles.input}
          type="email"
          value={inviteForm.email}
          disabled={isEdit}
          onChange={(e) =>
            setInviteForm((s) => ({ ...s, email: e.target.value }))
          }
        />
      </FormField>

      <FormField label="Password" required={!isEdit}>
        <input
          className={styles.input}
          type="password"
          value={inviteForm.password}
          onChange={(e) =>
            setInviteForm((s) => ({ ...s, password: e.target.value }))
          }
        />
      </FormField>

      <FormField label="Roles">
        <MultiSelect
          options={roleOptions}
          value={inviteForm.roles}
          onChange={(v) => setInviteForm((s) => ({ ...s, roles: v }))}
        />
      </FormField>

      <FormField label="Departments">
        <MultiSelect
          options={deptOptions}
          value={inviteForm.departments}
          onChange={(v) => setInviteForm((s) => ({ ...s, departments: v }))}
        />
      </FormField>
    </div>
  );
}

export default function UserList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [depts, setDepts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: "",
    email: "",
    password: "",
    roles: [],
    departments: [],
  });
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, d, r] = await Promise.all([
        getUsersAPI(),
        getDepartmentsAPI(),
        getRolesAPI(),
      ]);
      setRows(u.data || []);
      setDepts(d.data || []);
      setRoles(r.data || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((r) => r.status === "active").length,
      invited: rows.filter((r) => r.status === "invited").length,
      disabled: rows.filter((r) => r.status === "disabled").length,
    }),
    [rows],
  );

  const statItems = [
    {
      key: "total",
      label: "Total Users",
      icon: Users,
      count: counts.total,
      color: PRIMARY_COLOR,
      bg: "rgba(100,73,152,0.08)",
    },
    {
      key: "active",
      label: "Active",
      icon: UserCheck,
      count: counts.active,
      color: "#16a679",
      bg: "rgba(22,166,121,0.08)",
    },
    {
      key: "invited",
      label: "Invited",
      icon: Clock,
      count: counts.invited,
      color: "#d18a00",
      bg: "rgba(209,138,0,0.08)",
    },
    {
      key: "disabled",
      label: "Disabled",
      icon: UserX,
      count: counts.disabled,
      color: "#d03050",
      bg: "rgba(208,48,80,0.08)",
    },
  ];

  const roleOptions = useMemo(
    () => roles.map((r) => ({ label: r.name, value: r.roleKey })),
    [roles],
  );
  const deptOptions = useMemo(
    () => depts.map((d) => ({ label: d.name, value: d.departmentId })),
    [depts],
  );

  const columns = useMemo(
    () => [
      {
        name: "User",
        selector: (r) => r.name,
        sortable: true,
        grow: 2,
        cell: (r) => (
          <div className={styles.userCell}>
            <div className={styles.avatar}>
              {(r.name || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <div className={styles.userName}>{r.name || "—"}</div>
              <div className={styles.userEmail}>{r.email}</div>
            </div>
          </div>
        ),
      },
      {
        name: "Departments",
        cell: (r) => (
          <div className={styles.tagRow}>
            {r.departments?.slice(0, 2).map((d, i) => (
              <span key={i} className={styles.tag}>
                {d.departmentName}
              </span>
            ))}
            {r.departments?.length > 2 && (
              <span className={styles.tagMore}>
                +{r.departments.length - 2}
              </span>
            )}
          </div>
        ),
      },
      {
        name: "Roles",
        cell: (r) => (
          <div className={styles.tagRow}>
            {r.roles?.slice(0, 2).map((role, i) => (
              <span key={i} className={`${styles.tag} ${styles.tagRole}`}>
                {role}
              </span>
            ))}
            {r.roles?.length > 2 && (
              <span className={styles.tagMore}>+{r.roles.length - 2}</span>
            )}
          </div>
        ),
      },
      {
        name: "Status",
        width: "110px",
        cell: (r) => <StatusBadge status={r.status} />,
      },
      {
        name: "Actions",
        width: "120px",
        cell: (r) => (
          <div className={styles.actions}>
            <button
              className={styles.actionBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleViewUser(r);
              }}
              title="View details"
            >
              <Eye size={14} />
            </button>
            <button
              className={styles.actionBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleEditUser(r);
              }}
              title="Edit user"
            >
              <Edit2 size={14} />
            </button>
            <button
              className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteUser(r);
              }}
              title="Delete user"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  const handleViewUser = (user) => {
    navigate(companyUrl(`users/${user.userId || user._id}`));
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setInviteForm({
      name: user.name,
      email: user.email,
      password: "",
      roles: user.roles || [],
      departments: (user.departments || []).map((d) => d.departmentId),
    });
    setEditUserOpen(true);
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Delete user ${user.name}?`)) {
      try {
        await deleteUserAPI(user.userId || user._id);
        toast.success("User deleted");
        load();
      } catch (e) {
        toast.error(e.message);
      }
    }
  };

  const resetForm = useCallback(() => {
    setInviteForm({
      name: "",
      email: "",
      password: "",
      roles: [],
      departments: [],
    });
  }, []);

  const sendInvite = async () => {
    if (!inviteForm.name || !inviteForm.email || !inviteForm.password) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      const deptObjs = depts
        .filter((d) => inviteForm.departments.includes(d.departmentId))
        .map((d) => ({ departmentId: d.departmentId, departmentName: d.name }));
      await createUserAPI({ ...inviteForm, departments: deptObjs });
      toast.success("User created successfully");
      setInviteOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const updateUser = async () => {
    try {
      const deptObjs = depts
        .filter((d) => inviteForm.departments.includes(d.departmentId))
        .map((d) => ({ departmentId: d.departmentId, departmentName: d.name }));
      await updateUserAPI(editingUser.userId || editingUser._id, {
        name: inviteForm.name,
        roles: inviteForm.roles,
        departments: deptObjs,
        ...(inviteForm.password && { password: inviteForm.password }),
      });
      toast.success("User updated");
      setEditUserOpen(false);
      setEditingUser(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <PageHeader
        title="Users & Roles"
        breadcrumbs={[
          { label: "Company", path: companyUrl("dashboard") },
          { label: "Users" },
        ]}
        primaryAction={{
          label: "Add User",
          onClick: () => {
            resetForm();
            setInviteOpen(true);
          },
        }}
        secondaryActions={[
          {
            label: "Role Matrix",
            onClick: () => navigate(companyUrl("users/roles")),
          },
          { label: "Refresh", onClick: load },
        ]}
      />

      {/* Stat Cards */}
      <div className={styles.statsRow}>
        {statItems.map(({ key, label, icon: Icon, count, color, bg }) => (
          <div
            key={key}
            className={styles.statCard}
            style={{ "--accent": color, "--accent-bg": bg }}
          >
            <div className={styles.statIconWrap}>
              <Icon size={16} />
            </div>
            <div className={styles.statNumber}>{count}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      {!loading && rows.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title="No users found"
          description="Click 'Add User' to create your first user."
          actionLabel="Add User"
          onAction={() => {
            resetForm();
            setInviteOpen(true);
          }}
        />
      ) : (
        <DataTableWrapper
          columns={columns}
          data={rows}
          loading={loading}
          showSearch
          keyField="userId"
          onRowClick={(r) => handleViewUser(r)}
        />
      )}

      {/* ── Create User Modal ── */}
      <Modal
        show={inviteOpen}
        onHide={() => setInviteOpen(false)}
        centered
        size="md"
      >
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <h5 className={styles.modalTitle}>Add New User</h5>
            <button
              className={styles.modalClose}
              onClick={() => setInviteOpen(false)}
            >
              <X size={16} />
            </button>
          </div>
          <div className={styles.modalBody}>
            <UserForm
              isEdit={false}
              inviteForm={inviteForm}
              setInviteForm={setInviteForm}
              roleOptions={roleOptions}
              deptOptions={deptOptions}
            />
          </div>
          <div className={styles.modalFooter}>
            <button
              className={styles.btnOutline}
              onClick={() => setInviteOpen(false)}
            >
              Cancel
            </button>
            <button className={styles.btnPrimary} onClick={sendInvite}>
              Create User
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Edit User Modal ── */}
      <Modal
        show={editUserOpen}
        onHide={() => setEditUserOpen(false)}
        centered
        size="md"
      >
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <h5 className={styles.modalTitle}>Edit User</h5>
            <button
              className={styles.modalClose}
              onClick={() => setEditUserOpen(false)}
            >
              <X size={16} />
            </button>
          </div>
          <div className={styles.modalBody}>
            <UserForm
              isEdit={true}
              inviteForm={inviteForm}
              setInviteForm={setInviteForm}
              roleOptions={roleOptions}
              deptOptions={deptOptions}
            />
          </div>
          <div className={styles.modalFooter}>
            <button
              className={styles.btnOutline}
              onClick={() => setEditUserOpen(false)}
            >
              Cancel
            </button>
            <button className={styles.btnPrimary} onClick={updateUser}>
              Save Changes
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
